import React from 'react';
import { useHistory } from 'react-router-dom';
import { IonCard, IonCardContent, IonIcon, IonSkeletonText, IonText, IonButton } from '@ionic/react';
import {
  thermometerOutline,
  waterOutline,
  rainyOutline,
  compassOutline,
  settingsOutline,
  speedometerOutline,
  timeOutline,
  hardwareChipOutline,
} from 'ionicons/icons';
import type { Device } from '../../../domain/Device';
import { hasComponent, enabledOfKind, componentsSummary } from '../../../domain/Component';
import { LOCATION_SOURCE_LABEL } from '../../../domain/DeviceLocation';
import {
  compassPoint,
  dewPoint,
  formatRelative,
  round1,
  pressureTrend,
  soilMoistureLabel,
  stationInsights,
  type StationDetailSlug,
} from '../../../domain/WeatherStation';
import { useWeatherStation } from '../../../application/use-cases/weatherStation/useWeatherStation';
import { ConnectivityBanner } from '../ConnectivityStatus';
import { connectivityOf } from '../../../domain/connectivity';
import StationValves from './StationValves';
import StationInsights from './StationInsights';
import StationAgro from './StationAgro';
import { ForecastDays } from './StationForecast';
import HomeHero from '../home/HomeHero';
import { ConditionCard, MetricTiles, RecommendCard, SoilCard, type MetricTileItem } from '../home/HomeCards';
import { useSkyMood } from '../home/useSkyMood';
import type { HeroExtras } from '../home/HomeHero';
import { fieldCondition } from '../../../domain/FieldCondition';
import './station.css';

interface Props {
  station: Device;
  // Lo del Inicio que no es de la estación: el selector y las alertas.
  hero?: HeroExtras;
  // El pronóstico de 7 días va solo en el Inicio, no al abrir la estación desde un lote.
  showForecast?: boolean;
}

const StationHome: React.FC<Props> = ({ station, hero, showForecast = true }) => {
  const history = useHistory();
  const { device, overview, loading, error, forecast, forecastStatus, weatherLocation, latestAt } = useWeatherStation(station.id);
  const current = device ?? station;
  const components = current.components;
  const hasAir = hasComponent(components, 'air');
  const hasPressure = hasComponent(components, 'pressure');
  const hasRain = hasComponent(components, 'rain');
  const hasWind = hasComponent(components, 'wind');
  const soilProbes = overview?.soil ?? [];
  const hasSensors = hasAir || hasPressure || hasRain || hasWind || enabledOfKind(components, 'soil').length > 0;
  const hasValves = enabledOfKind(components, 'valve').length > 0;

  const go = (slug: StationDetailSlug) => history.push(`/app/stations/${station.id}/${slug}`);

  const temp = overview?.current.temperature?.value ?? null;
  const rh = overview?.current.humidity?.value ?? null;
  const pressure = overview?.current.pressure?.value ?? null;
  const trend = overview ? pressureTrend(overview.series.pressure, overview.current.pressure) : null;
  const wind = overview?.current.windSpeed?.value ?? null;
  const windDir = overview?.current.windDirection?.value ?? null;
  const hasReadings = overview
    ? Object.values(overview.current).some((r) => r !== null) || overview.soil.some((p) => p.current !== null)
    : false;
  const connectivity = device ? connectivityOf(device) : null;
  const nowCode = forecast?.hourly[0]?.weatherCode;

  const insights =
    hasAir && overview && hasReadings
      ? stationInsights(
          temp,
          rh,
          wind,
          overview.rain.lastHour,
          overview.stats.temperature?.min ?? null,
          current.thresholds,
          forecast?.hourly
        )
      : [];
  const mainSoil = soilProbes.find((p) => p.current !== null) ?? null;
  const soilState = mainSoil?.current ? soilMoistureLabel(mainSoil.current.value, current.thresholds.soil) : null;
  const condition = fieldCondition(insights, soilState);
  const spray = insights.find((i) => i.kind === 'spray');
  const mood = useSkyMood({
    temperature: temp,
    windKmh: wind,
    rainLastHour: hasRain && overview ? overview.rain.lastHour : null,
    weatherCode: nowCode ?? null,
  });

  const tiles: MetricTileItem[] = [];
  if (overview && hasAir) {
    tiles.push({
      key: 'temp',
      tone: 'temp',
      icon: thermometerOutline,
      value: temp !== null ? `${round1(temp)}°` : '—',
      label: 'Temp.',
      sub: overview.stats.temperature
        ? `${round1(overview.stats.temperature.min)}° · ${round1(overview.stats.temperature.max)}°`
        : undefined,
      onClick: () => go('temperatura'),
    });
    tiles.push({
      key: 'rh',
      tone: 'humidity',
      icon: waterOutline,
      value: rh !== null ? `${Math.round(rh)}%` : '—',
      label: 'Humedad',
      sub: temp !== null && rh !== null ? `Rocío ${round1(dewPoint(temp, rh))}°` : undefined,
      onClick: () => go('humedad'),
    });
  }
  if (overview && hasWind) {
    tiles.push({
      key: 'wind',
      tone: 'wind',
      icon: compassOutline,
      value: wind !== null ? `${Math.round(wind)}` : '—',
      unit: 'km/h',
      label: 'Viento',
      sub: overview.stats.windSpeed
        ? `Ráf. ${Math.round(overview.stats.windSpeed.max)} km/h`
        : windDir !== null
          ? `Del ${compassPoint(windDir)}`
          : undefined,
      onClick: () => go('viento'),
    });
  }
  if (overview && hasRain) {
    tiles.push({
      key: 'rain',
      tone: 'rain',
      icon: rainyOutline,
      value: `${overview.rain.today}`,
      unit: 'mm',
      label: 'Lluvia',
      sub: overview.rain.lastHour > 0 ? `${overview.rain.lastHour} mm/h` : `7d ${Math.round(overview.rain.last7d)} mm`,
      onClick: () => go('lluvia'),
    });
  }
  if (overview && hasPressure) {
    tiles.push({
      key: 'pressure',
      tone: 'pressure',
      icon: speedometerOutline,
      value: pressure !== null ? `${Math.round(pressure)}` : '—',
      unit: 'hPa',
      label: 'Presión',
      sub: trend ? trend.label : undefined,
      onClick: () => go('presion'),
    });
  }

  const meta = [
    ...(latestAt ? [{ icon: timeOutline, text: formatRelative(latestAt) }] : []),
    ...(hasSensors ? [{ icon: hardwareChipOutline, text: componentsSummary(components) }] : []),
  ];

  return (
    <section className={`station-home${connectivity?.state === 'offline' ? ' is-offline' : ''}`}>
      <HomeHero
        mood={mood}
        title={current.name}
        status={
          connectivity
            ? {
                label: connectivity.label,
                online: connectivity.state === 'online',
              }
            : undefined
        }
        meta={meta}
        switcher={hero?.switcher}
        alertCount={hero?.alertCount ?? 0}
        onBack={hero?.onBack}
        onSettings={() => history.push(`/app/devices/${station.id}`)}
      >
        {condition && (
          <ConditionCard
            label="Condición de la estación"
            condition={condition}
            onClick={current.access !== 'view' ? () => history.push(`/app/devices/${station.id}/thresholds`) : undefined}
          />
        )}
      </HomeHero>

      <div className="home-body">
        {device && <ConnectivityBanner device={device} />}

        {!hasSensors && !hasValves && (
          <IonCard>
            <IonCardContent className="station-onboarding">
              <p>
                <strong>Esta estación todavía no tiene sensores.</strong> Indicá qué tiene enchufado (clima, pluviómetro,
                viento, sondas de suelo, válvulas) o esperá su primer mensaje: lo que mande se agrega solo.
              </p>
              <IonButton fill="outline" size="small" onClick={() => history.push(`/app/devices/${station.id}`)}>
                <IonIcon icon={settingsOutline} slot="start" />
                Configurar estación
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}

        {hasSensors && loading && !overview && (
          <div className="home-metrics">
            {[0, 1, 2, 3].map((i) => (
              <IonSkeletonText
                key={i}
                animated
                style={{
                  width: '100%',
                  height: 116,
                  borderRadius: 20,
                  margin: 0,
                }}
              />
            ))}
          </div>
        )}

        {hasSensors && error && !overview && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {hasSensors && overview && !hasReadings && (
          <IonCard>
            <IonCardContent className="station-onboarding">
              <p>
                <strong>Esperando la primera lectura.</strong> Apenas la estación empiece a transmitir, vas a ver acá
                sus datos.
              </p>
            </IonCardContent>
          </IonCard>
        )}

        {hasSensors && overview && hasReadings && (
          <>
            {soilProbes.map((probe) =>
              probe.current ? (
                <SoilCard
                  key={probe.channel}
                  title={soilProbes.length > 1 ? probe.name : 'Humedad del suelo'}
                  value={probe.current.value}
                  lowPct={current.thresholds.soil.lowPct}
                  saturatedPct={current.thresholds.soil.saturatedPct}
                  state={soilMoistureLabel(probe.current.value, current.thresholds.soil).label}
                  onClick={() => history.push(`/app/stations/${station.id}/suelo/${probe.channel}`)}
                />
              ) : null
            )}
            {tiles.length > 0 && <MetricTiles items={tiles} />}
            {spray && <RecommendCard title={spray.title} detail={spray.detail} onClick={() => history.push('/app/ai')} />}
          </>
        )}
      </div>

      {hasValves && <StationValves deviceId={station.id} canControl={current.access !== 'view'} />}

      {insights.length > 0 && (
        <StationInsights
          items={insights}
          settingsHref={current.access !== 'view' ? `/app/devices/${station.id}/thresholds` : undefined}
          customized={current.customThresholds}
        />
      )}

      {(hasAir || hasRain) && overview && hasReadings && <StationAgro deviceId={station.id} hasAir={hasAir} />}

      {showForecast && (
        <IonCard className="station-forecast-card">
          <IonCardContent>
            <div className="station-section-head">
              <span>Pronóstico 7 días</span>
              <span className="station-source">
                {weatherLocation ? `${LOCATION_SOURCE_LABEL[weatherLocation.source]} · ` : ''}
                vía Open-Meteo
              </span>
            </div>
            {forecastStatus === 'loading' && (
              <IonSkeletonText
                animated
                style={{
                  width: '100%',
                  height: 220,
                  borderRadius: 10,
                  margin: 0,
                }}
              />
            )}
            {forecastStatus === 'ok' && forecast && <ForecastDays days={forecast.daily} />}
            {forecastStatus === 'error' && <p className="station-empty">No se pudo obtener el pronóstico.</p>}
            {forecastStatus === 'unset' && (
              <>
                <p className="station-empty">
                  La estación no tiene ubicación todavía. Si el equipo tiene GPS se carga sola con su próximo mensaje; si no, marcala en el
                  mapa (o asignale un campo).
                </p>
                <IonButton expand="block" fill="outline" onClick={() => history.push(`/app/devices/${station.id}`)}>
                  Cargar ubicación de la estación
                </IonButton>
              </>
            )}
          </IonCardContent>
        </IonCard>
      )}
    </section>
  );
};

export default StationHome;
